import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { Button, Chip } from '@mes-amis/galette-design-system';
import db, { Document, isTemplateOfflineEnabled, arePrefillsDisabled } from '@/db';
import { dispatchDataEvent, DataEvents } from '@/db/events';
import { trackEvent } from '@/db/services/analytics_service';
import { createDraftFromLockedOnlineConflict, prefetchDocumentPdfs, trackDocumentDiscarded, isPendingDocument } from '@/db/services/document_service';
import { buildDuplicatePrefills } from '@/db/services/duplicate_prefill';
import { buildPrefillFormResolvers, type SuperFormDefinition } from '@mes-amis/super-form';
import { isUnderReview } from '@/db/types/document';
import { lockedOnlineCopy } from '@/lib/locked_online';
import { documentPath } from '@/utils/routes';
import DocumentStatusPill, { getDisplayStatus } from '../DocumentStatusPill';
import PdfLink from '../PdfLink';
import ChangeProgramSiteModal from '../modals/ChangeProgramSiteModal';
import LockedOnlineConflictModal from '../modals/LockedOnlineConflictModal';
import { useCurrentPerson } from '../../hooks/useCurrentPerson';
import { ChevronDownIcon, ArrowPathIcon, DocumentDuplicateIcon, TrashIcon, EllipsisVerticalIcon, PencilSquareIcon } from '@heroicons/react/24/outline';

/* ── More-actions dropdown (⋮ button) ─────────── */

interface MoreActionsMenuProps {
  onDuplicate: (e: React.MouseEvent) => void;
  showDuplicate?: boolean;
  onChangeProgramSite?: (e: React.MouseEvent) => void;
  showChangeProgramSite?: boolean;
}

function MoreActionsMenu({ onDuplicate, showDuplicate = true, onChangeProgramSite, showChangeProgramSite }: MoreActionsMenuProps) {
  const [open, setOpen] = useState(false);

  // Nothing to show — don't render the trigger at all.
  if (!showDuplicate && !showChangeProgramSite) return null;

  return (
    <div
      className="relative"
      onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}
    >
      <button
        onClick={() => setOpen(prev => !prev)}
        className="p-2 -m-0.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="More actions"
      >
        <EllipsisVerticalIcon className="h-5 w-5" />
      </button>

      {open && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />

          {/* Menu */}
          <div role="menu" className="absolute right-0 top-full mt-1 z-20 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[180px]">
            {showDuplicate && (
              <button
                role="menuitem"
                onClick={(e) => { setOpen(false); onDuplicate(e); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <DocumentDuplicateIcon className="h-4 w-4" />
                Duplicate
              </button>
            )}
            {showChangeProgramSite && onChangeProgramSite && (
              <button
                role="menuitem"
                onClick={(e) => { setOpen(false); onChangeProgramSite(e); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <PencilSquareIcon className="h-4 w-4" />
                Edit Program/Site
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

interface DocumentsTabProps {
  clientId: number;
  clientSlug: string;
  onNewDocument: () => void;
  onShowPdf?: (doc: Document) => void;
}

const DocumentsTab: React.FC<DocumentsTabProps> = ({ clientId, clientSlug, onNewDocument, onShowPdf }) => {
  const navigate = useNavigate();

  // Get current person for organization scoping and permissions
  const { currentPerson, organizationId } = useCurrentPerson();

  // Use useLiveQuery to get documents from the database
  const rawDocuments = useLiveQuery(async () => {
    if (!clientId) return [];

    return await db.documents
      .where('client_id')
      .equals(clientId)
      .toArray();
  }, [clientId]);

  // Load document templates for use_offline checks, scoped by organization
  const documentTemplates = useLiveQuery(async () => {
    if (!organizationId) return [];

    return await db.documentTemplates
      .where('organization_id')
      .equals(organizationId)
      .toArray();
  }, [organizationId]);

  // Helper to check if a document's template supports offline use.
  // Defaults to false when templates haven't loaded or template not found,
  // so we don't accidentally route PDF-only documents to the form editor.
  const isDocTemplateOfflineEnabled = useCallback((doc: Document): boolean => {
    if (!documentTemplates) return false;
    const template = documentTemplates.find(t => t.id === doc.template_id);
    if (!template) return false;
    return isTemplateOfflineEnabled(template);
  }, [documentTemplates]);

  // Whether a document may be duplicated. Templates that disable prefills hide
  // the Duplicate action so assessors start fresh. Defaults to allowed until
  // templates load, matching prior behavior.
  const isDocDuplicable = useCallback((doc: Document): boolean => {
    if (!documentTemplates) return true;
    const template = documentTemplates.find(t => t.id === doc.template_id);
    return !arePrefillsDisabled(template);
  }, [documentTemplates]);

  // Load signatures for all documents belonging to this client
  const signatures = useLiveQuery(async () => {
    if (!rawDocuments || rawDocuments.length === 0) return [];
    const docIds = rawDocuments.map(d => d.id).filter((id): id is number | string => id != null);
    if (docIds.length === 0) return [];
    return db.signatures.where('document_id').anyOf(docIds).toArray();
  }, [rawDocuments]);

  // Build a lookup: document_id → signed count from local signatures table
  const localSignedCounts = useMemo(() => {
    const map = new Map<number | string, number>();
    if (!signatures) return map;
    for (const sig of signatures) {
      if (sig.status === 'completed' || sig.local_completed) {
        map.set(sig.document_id, (map.get(sig.document_id) ?? 0) + 1);
      }
    }
    return map;
  }, [signatures]);

  // Helper: get signature counts for a document using template config + local data
  const getSignatureCounts = useCallback((doc: Document): { total: number; signed: number } | null => {
    if (!documentTemplates) return null;
    const template = documentTemplates.find(t => t.id === doc.template_id);
    if (!template?.signature_config) return null;

    const config = template.signature_config;
    const total = (config.required_titles?.length ?? 0) + (config.required_people?.length ?? 0);
    if (total === 0) return null;

    // If completed, all signatures are done
    if (doc.status === 'completed') return { total, signed: total };

    // Only documents inside the signing window carry signatures. Anything else
    // (pending, draft, re-opened) has no active ceremony — re-opening
    // invalidates it, so local signature rows from before the re-open are stale.
    if (doc.status !== 'waiting_for_signature' && doc.status !== 'signing_in_progress') return null;

    const signed = doc.id != null ? (localSignedCounts.get(doc.id) ?? 0) : 0;
    return { total, signed };
  }, [documentTemplates, localSignedCounts]);

  // Sort documents by created_at date (newest first)
  const allDocuments = rawDocuments ?
    [...rawDocuments].sort((a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    ) : [];

  const loading = rawDocuments === undefined;
  const [error, setError] = useState<string | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());


  // Delete confirmation state
  const [documentToDelete, setDocumentToDelete] = useState<number | string | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  // Sync state
  const [syncingDocuments, setSyncingDocuments] = useState<Set<number | string>>(new Set());

  // Handle delete confirmation
  const handleDeleteConfirm = useCallback(async () => {
    if (!documentToDelete) return;

    try {
      // A deleted_online row that stranded unsynced edits survives the refresh
      // GC precisely so its values stay recoverable — deleting it here is the
      // moment they're given up, so record them as a document.discarded.
      const doc = await db.documents.get(documentToDelete);
      if (doc?.sync_error_type === 'deleted_online' && doc.deleted_online_had_unsynced_edits) {
        trackDocumentDiscarded(doc);
      }

      // Delete locally from IndexedDB only
      // Only unsynced documents (negative IDs or is_offline_draft) can be deleted
      await db.documents.delete(documentToDelete);

      // Track document deletion
      await trackEvent('document.deleted', {
        document_id: documentToDelete,
        client_id: clientId,
      });

      // useLiveQuery will automatically update when document is deleted from database

      // Reset delete confirmation state
      setDocumentToDelete(null);
      setShowDeleteConfirmation(false);
      setError(null);
    } catch (err) {
      console.error('Error deleting document:', err);
      setError(`Failed to delete document: ${err instanceof Error ? err.message : 'Unknown error'}`);
      setShowDeleteConfirmation(false);
    }
  }, [documentToDelete, clientId]);

  // Load programs and sites from DB for name lookups
  const allPrograms = useLiveQuery(async () => {
    if (!organizationId) return [];
    return db.programs.where('organization_id').equals(organizationId).toArray();
  }, [organizationId]) ?? [];

  const allSites = useLiveQuery(async () => {
    if (!organizationId) return [];
    return db.sites.where('organization_id').equals(organizationId).toArray();
  }, [organizationId]) ?? [];

  // Build lookup maps for program and site names
  const programNameMap = useMemo(() => {
    const map = new Map<number, string>();
    allPrograms.forEach(p => map.set(p.id, p.name));
    return map;
  }, [allPrograms]);

  const siteNameMap = useMemo(() => {
    const map = new Map<number, string>();
    allSites.forEach(s => map.set(s.id, s.name));
    return map;
  }, [allSites]);

  // Check if a document can be deleted
  const canDeleteDocument = useCallback((doc: Document) => {
    // Allow deleting documents that were deleted online (cleanup local copy)
    if (doc.sync_error_type === 'deleted_online') return true;
    // Only allow deleting unsynced offline documents
    // (draft with string ID or unsynced document with local_id)
    // Synced documents cannot be deleted to prevent data loss
    return (typeof doc.id === 'string') || (doc.local_id !== undefined && !doc.synced);
  }, []);

  // Open delete confirmation dialog
  const handleDeleteClick = useCallback((e: React.MouseEvent, doc: Document) => {
    e.stopPropagation(); // Prevent triggering the row click

    // Check if document can be deleted
    if (!canDeleteDocument(doc)) {
      // Document is synced - cannot be deleted
      setError('Cannot delete synced documents. Only unsynced offline drafts can be deleted to prevent data loss.');
      return;
    }

    if (doc.id !== undefined) {
      setDocumentToDelete(doc.id);
      setShowDeleteConfirmation(true);
    }
  }, [canDeleteDocument]);

  // Handle duplicating a document
  const handleDuplicateClick = useCallback(async (e: React.MouseEvent, doc: Document) => {
    e.stopPropagation();
    setError(null);

    try {
      const now = new Date().toISOString();
      const uuid = crypto.randomUUID();
      const localId = `draft-${uuid}`;

      // Copied answers become confirm-required prefills. The client's
      // core_data_snapshot stands in for the live person record the server
      // reads when it re-derives core-data slugs on sync (GHIB-543).
      const template = documentTemplates?.find(t => t.id === doc.template_id);
      const clientRecord = await db.clients.get(clientId);
      const duplicatePrefills = buildDuplicatePrefills(
        doc.super_form as Record<string, unknown> | undefined,
        template?.super_form?.definition as SuperFormDefinition | undefined,
        { name: doc.name, completedAt: doc.completed_at },
        template,
        clientRecord?.core_data_snapshot,
      );
      // Only completed (already-synced) docs are duplicable, so a numeric server id is expected.
      const duplicatedFromId = typeof doc.id === 'number' ? doc.id : undefined;

      const newDocument = {
        id: uuid,
        local_id: localId,
        name: doc.name,
        client_id: clientId,
        template_id: doc.template_id,
        template_label: doc.template_label,
        status: 'draft',
        super_form: doc.super_form ? { ...doc.super_form } : {},
        ...(doc.field_permissions ? { field_permissions: doc.field_permissions } : {}),
        ...(Object.keys(duplicatePrefills).length ? { field_prefills: duplicatePrefills } : {}),
        ...(duplicatedFromId ? { duplicated_from_id: duplicatedFromId } : {}),
        created_at: now,
        updated_at: now,
        synced: false,
        ...(doc.program_id ? { program_id: doc.program_id } : {}),
        ...(doc.site_id ? { site_id: doc.site_id } : {}),
        ...(currentPerson ? {
          creator_id: currentPerson.id,
          creator: { id: currentPerson.id, full_name: currentPerson.official_full_name ?? '' },
        } : {}),
      };

      await db.documents.add(newDocument);

      await trackEvent('document.duplicated', {
        source_document_id: doc.id,
        client_id: clientId,
        template_id: doc.template_id,
      });

      navigate(`/clients/${clientSlug}/documents/drafts/${localId}/edit`);
    } catch (err) {
      console.error('Error duplicating document:', err);
      setError(`Failed to duplicate document: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }, [clientId, clientSlug, navigate, documentTemplates, currentPerson]);

  // Function to sync all unsynced documents via event system
  const syncAllUnsyncedDocuments = useCallback(() => {
    if (!navigator.onLine || !clientId) return;

    // Get all unsynced documents that have super_form
    const unsyncedDocuments = allDocuments.filter(isPendingDocument);

    if (unsyncedDocuments.length === 0) return;

    // Add all unsynced documents to syncing set
    setSyncingDocuments(prev => {
      const newSet = new Set(prev);
      unsyncedDocuments.forEach(doc => {
        if (doc.id !== undefined) {
          newSet.add(doc.id);
        }
      });
      return newSet;
    });

    // Deliberately NOT forced. Despite the name this is not a button: it runs on
    // mount and on every online / server-reachable event, which is the reconnect
    // path from the incident. Forcing here would re-send every held payload each
    // time an assessor opens a client's documents.
    dispatchDataEvent(DataEvents.SYNC_DOCUMENTS)
      .then(() => {
        // Clear syncing state after successful sync
        setTimeout(() => {
          setSyncingDocuments(new Set());
        }, 2000);
      })
      .catch(() => {
        // Sync failed (likely offline) — clear syncing state silently.
        // The documents remain unsynced and will retry next time we're online.
        setSyncingDocuments(prev => prev.size > 0 ? new Set() : prev);
      });
  }, [clientId, allDocuments]);

  // Auto-sync when component mounts with unsynced documents
  const hasSynced = React.useRef(false);
  useEffect(() => {
    if (!loading && allDocuments.length > 0 && !hasSynced.current) {
      hasSynced.current = true;
      syncAllUnsyncedDocuments();
    }
  }, [loading, allDocuments.length, syncAllUnsyncedDocuments]);

  // Re-sync when browser or server connectivity is restored
  useEffect(() => {
    const handleOnline = () => syncAllUnsyncedDocuments();
    window.addEventListener('online', handleOnline);
    window.addEventListener('server-reachable', handleOnline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('server-reachable', handleOnline);
    };
  }, [syncAllUnsyncedDocuments]);

  // Auto-prefetch PDFs that are visible in the UI but not yet cached locally.
  // Runs on mount/when docs change (while online) and on reconnect, so a greyed
  // offline icon self-heals once connectivity returns.
  useEffect(() => {
    if (!rawDocuments || rawDocuments.length === 0) return;

    const prefetchMissingPdfs = () => {
      if (!navigator.onLine) return;
      const missing = rawDocuments.filter(
        (d) =>
          d.links?.pdf &&
          !d.pdfContent &&
          (!d.super_form || d.status === 'completed')
      );
      if (missing.length === 0) return;
      prefetchDocumentPdfs(missing, 3).catch((err) =>
        console.error('Auto-prefetch of missing PDFs failed', err)
      );
    };

    prefetchMissingPdfs();
    window.addEventListener('server-reachable', prefetchMissingPdfs);
    return () => window.removeEventListener('server-reachable', prefetchMissingPdfs);
  }, [rawDocuments]);

  // Documents are automatically loaded via useLiveQuery, no manual fetching needed

  // Show PDF for a document - uses callback prop or navigates to PDF page
  const showPdf = useCallback((doc: Document) => {
    if (onShowPdf) {
      onShowPdf(doc);
      return;
    }
    const isDraft = typeof doc.id === 'string' && String(doc.id).startsWith('draft-');
    const route = isDraft
      ? `/clients/${clientSlug}/documents/drafts/${doc.id}/pdf`
      : `/clients/${clientSlug}/documents/${doc.id}/pdf`;
    navigate(route);
  }, [onShowPdf, clientSlug, navigate]);

  // Handle document click - documents with super_form go to edit, PDFs navigate to PDF page
  const handleDocumentClick = useCallback(async (doc: Document) => {
    // Track document viewing
    await trackEvent('document.viewed', {
      document_id: doc.id,
      client_id: clientId,
      template_id: doc.template_id,
      status: doc.status,
    });

    // Deleted online documents — navigate to view (readonly)
    if (doc.sync_error_type === 'deleted_online') {
      const editRoute = documentPath(clientSlug, doc);
      if (editRoute) navigate(editRoute);
      return;
    }

    const offlineEnabled = isDocTemplateOfflineEnabled(doc);

    // If template doesn't support offline use
    if (!offlineEnabled) {
      // For completed documents with PDF, show the PDF. Navigate immediately —
      // the PDF view fetches missing content itself (including polling a
      // still-rendering PDF) behind its own loading and retry UI, so the tap
      // gets instant feedback instead of blocking on the fetch.
      if (doc.status === 'completed' && (doc.pdfContent || doc.links?.pdf)) {
        showPdf(doc);
        return;
      }

      // Non-completed docs or completed without PDF are non-clickable (handled in render)
      return;
    }

    // If document has super_form (it's an assessment) or is pending/in_progress, navigate to edit
    if (doc.super_form !== undefined || doc.status === 'pending' || doc.status === 'in_progress' || doc.status === 'draft' || isUnderReview(doc)) {
      const editRoute = documentPath(clientSlug, doc);
      if (editRoute) navigate(editRoute);
      return;
    }

    // For other documents (e.g. completed without super_form), navigate to PDF page
    showPdf(doc);
  }, [clientId, clientSlug, navigate, showPdf, isDocTemplateOfflineEnabled]);

  // Group documents by template_id or name
  const documentGroups = useMemo(() => {
    // Create a map to group documents
    const groups = new Map<string, Document[]>();

    allDocuments.forEach(doc => {
      // Group by template_id + name so documents with different names aren't grouped together
      const groupKey = `template_${doc.template_id}_${doc.name}`;

      if (!groups.has(groupKey)) {
        groups.set(groupKey, []);
      }
      groups.get(groupKey)!.push(doc);
    });

    // Sort each group by updated_at descending (most recent first)
    groups.forEach(group => {
      group.sort((a, b) =>
        new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
      );
    });

    return groups;
  }, [allDocuments]);

  // Get the display list of documents (collapsed or expanded groups)
  const displayDocuments = useMemo(() => {
    const result: Document[] = [];

    // Get all groups sorted by the most recent document's updated_at
    const sortedGroups = Array.from(documentGroups.entries()).sort((a, b) => {
      const aNewest = a[1][0]; // First document is the most recent due to sorting
      const bNewest = b[1][0];
      return new Date(bNewest.updated_at).getTime() - new Date(aNewest.updated_at).getTime();
    });

    sortedGroups.forEach(([groupKey, documents]) => {
      if (expandedGroups.has(groupKey) && documents.length > 1) {
        // If expanded, show all documents in the group
        // Documents are already sorted by updated_at desc in documentGroups
        result.push(...documents);
      } else {
        // If collapsed, show only the most recent
        result.push(documents[0]);
      }
    });

    return result;
  }, [documentGroups, expandedGroups]);

  // Resolve the display name for a document: prefer template_label, then name,
  // then look up the template's label from the loaded templates table.
  const getDocDisplayName = useCallback((doc: Document): string => {
    if (doc.template_label) return doc.template_label;
    if (doc.name) return doc.name;
    const template = documentTemplates?.find(t => t.id === doc.template_id);
    return template?.name || template?.label || 'Untitled';
  }, [documentTemplates]);

  // Helper function to get group key for a document
  const getGroupKey = (doc: Document) => {
    return `template_${doc.template_id}_${doc.name}`;
  };

  // Helper function to get group info for a document
  const getGroupInfo = (doc: Document) => {
    const groupKey = getGroupKey(doc);
    const group = documentGroups.get(groupKey);
    if (!group) return { count: 1, isFirst: true, isExpanded: false };

    const isFirst = group[0].id === doc.id;
    const isExpanded = expandedGroups.has(groupKey);
    return { count: group.length, isFirst, isExpanded };
  };

  // Toggle group expansion
  const toggleGroupExpansion = (groupKey: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupKey)) {
        newSet.delete(groupKey);
      } else {
        newSet.add(groupKey);
      }
      return newSet;
    });
  };

  // Change Program & Site modal state
  const [changeProgramSiteDoc, setChangeProgramSiteDoc] = useState<Document | null>(null);

  // Locked-online conflict-resolution modal state
  const [conflictDoc, setConflictDoc] = useState<Document | null>(null);

  // Slug → form-label resolver for the conflict modal's diff table, built from
  // the conflicted document's template (same resolver the prefill UI uses).
  const conflictLabelResolver = useMemo(() => {
    const definition = documentTemplates?.find(t => t.id === conflictDoc?.template_id)?.super_form?.definition;
    return definition ? buildPrefillFormResolvers(definition.form, definition.fields).resolveLabel : undefined;
  }, [documentTemplates, conflictDoc?.template_id]);

  // Seed a new local draft from the edits kept when the document was locked online,
  // then discard the conflict on the original (adopting the completed server copy).
  const handleRecreateFromConflict = useCallback(async (doc: Document) => {
    const template = documentTemplates?.find(t => t.id === doc.template_id);
    const localId = await createDraftFromLockedOnlineConflict(doc, template, clientId, currentPerson);
    setConflictDoc(null);
    navigate(`/clients/${clientSlug}/documents/drafts/${localId}/edit`);
  }, [documentTemplates, clientId, currentPerson, clientSlug, navigate]);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="font-heading text-2xl text-black">Documents</h2>
        <Button variant="primary" onClick={onNewDocument}>Add New Document</Button>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded" role="alert">
          <p className="font-medium">Error</p>
          <p>{error}</p>
        </div>
      )}

      {/* Documents table */}
      {!loading && displayDocuments.length > 0 ? (
        <div className="border border-[#ddd] rounded">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-[#f9f9f9] border-b border-[#ddd] text-xs font-semibold text-gray-900">
                <th className="h-11 px-3 font-semibold text-left">Document name</th>
                <th className="h-11 px-3 font-semibold text-left">Added by</th>
                <th className="h-11 px-3 font-semibold text-left">Created</th>
                <th className="h-11 px-3 font-semibold text-left">Completed</th>
                <th className="h-11 px-3 font-semibold text-center">Signatures</th>
                <th className="h-11 px-3 font-semibold text-left">Status</th>
                <th className="h-11 px-3 font-semibold text-right">Actions</th>
                <th className="h-11" />
              </tr>
            </thead>
            <tbody>
          {displayDocuments.map((doc) => {
            const groupInfo = getGroupInfo(doc);
            const groupKey = getGroupKey(doc);
            const offlineEnabled = isDocTemplateOfflineEnabled(doc);
            const isDeletedOnline = doc.sync_error_type === 'deleted_online';
            const hasViewablePdf = doc.status === 'completed' && (doc.pdfContent || doc.links?.pdf);
            const isRowClickable = isDeletedOnline || offlineEnabled || hasViewablePdf;

            return (
              <tr
                key={doc.id}
                className={`border-b border-[#eee] last:border-b-0 transition-colors ${
                  isRowClickable ? 'hover:bg-gray-50 cursor-pointer' : 'opacity-60'
                } ${groupInfo.count > 1 && !groupInfo.isFirst ? 'bg-gray-50/50' : 'bg-white'}`}
                onClick={isRowClickable ? () => void handleDocumentClick(doc) : undefined}
              >
                {/* Document name */}
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {/* Expand/collapse chevron */}
                    {groupInfo.isFirst && groupInfo.count > 1 ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleGroupExpansion(groupKey); }}
                        className="p-2 -m-1.5 hover:bg-gray-200 rounded-lg shrink-0"
                      >
                        <ChevronDownIcon className={`h-5 w-5 text-gray-500 transition-transform ${groupInfo.isExpanded ? 'rotate-0' : '-rotate-90'}`} />
                      </button>
                    ) : (
                      <span className="w-5 shrink-0" />
                    )}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-black truncate">
                          {getDocDisplayName(doc)}
                        </span>
                        {groupInfo.isFirst && groupInfo.count > 1 && !groupInfo.isExpanded && (
                          <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded shrink-0">
                            {groupInfo.count}
                          </span>
                        )}
                        {!doc.synced && doc.super_form && (
                          <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded shrink-0">
                            Unsynced
                          </span>
                        )}
                      </div>
                      {(doc.program_id || doc.site_id) && (
                        <span className="text-xs text-gray-500 truncate block">
                          {[
                            doc.program_id && programNameMap.get(doc.program_id),
                            doc.site_id && siteNameMap.get(doc.site_id),
                          ].filter(Boolean).join(' / ')}
                        </span>
                      )}
                    </div>
                  </div>
                </td>

                {/* Added by */}
                <td className="px-3 py-2 text-xs text-gray-600">
                  {doc.creator?.full_name || '—'}
                </td>

                {/* Created At */}
                <td className="px-3 py-2 text-xs text-gray-600">
                  {doc.created_at ? new Date(doc.created_at).toLocaleDateString() : '—'}
                </td>

                {/* Completed date — mirrors the online list's display_date
                    (recorded_at.presence || completed_at): recorded_at is the
                    user-entered "date originally completed", preferred over the
                    completed_at system-finalization timestamp. */}
                <td className="px-3 py-2 text-xs text-gray-600">
                  {(() => {
                    const displayDate = doc.recorded_at ?? doc.completed_at;
                    return displayDate ? new Date(displayDate).toLocaleDateString() : '—';
                  })()}
                </td>

                {/* Signatures */}
                <td className="px-3 py-2 text-xs text-gray-600 text-center">
                  {(() => {
                    const counts = getSignatureCounts(doc);
                    if (!counts) return '—';
                    const label = (
                      <span className={counts.signed === counts.total ? 'text-green-700' : ''}>
                        {counts.signed}/{counts.total}
                      </span>
                    );
                    const editRoute = documentPath(clientSlug, doc);
                    if (!editRoute) return label;
                    return (
                      // stopPropagation is required here: the row itself is clickable
                      <Link
                        to={`${editRoute}?tab=signature`}
                        className="underline hover:text-gray-900"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {label}
                      </Link>
                    );
                  })()}
                </td>

                {/* Status */}
                <td className="px-3 py-2">
                  {doc.sync_error_type === 'completed_online' ? (
                    <Chip
                      color="yellow"
                      size="md"
                      onClick={(e) => { e.stopPropagation(); setConflictDoc(doc); }}
                    >
                      {lockedOnlineCopy(doc).chip}
                    </Chip>
                  ) : getDisplayStatus(doc) === 'permission_sync_error' ? (
                    <Chip
                      color="red"
                      size="md"
                      onClick={(e) => { e.stopPropagation(); setChangeProgramSiteDoc(doc); }}
                    >
                      Permission Error
                    </Chip>
                  ) : (
                    <DocumentStatusPill document={doc} hasSignatureFlow={getSignatureCounts(doc) !== null} />
                  )}
                  {!offlineEnabled && doc.status !== 'completed' && (
                    <span className="text-[10px] text-gray-400 block mt-0.5">Online only</span>
                  )}
                </td>

                {/* Actions */}
                <td className="px-3 py-2 cursor-default">
                  <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                    {/* PDF download */}
                    {doc.links?.pdf && (!doc.super_form || doc.status === 'completed') && (
                      <PdfLink
                        doc={doc}
                        clientSlug={clientSlug}
                        disabled={!navigator.onLine && !doc.pdfContent}
                      />
                    )}

                    {/* Sync indicator*/}
                    {!doc.synced && doc.super_form && (
                       <span className="p-2 -m-0.5 text-gray-400" title={syncingDocuments.has(doc.id!) ? 'Syncing…' : 'Not synced'}
                      >
                        {syncingDocuments.has(doc.id!) ? (
                          <div className="h-5 w-5 border-2 border-gray-300 border-t-deep-sea-green rounded-full animate-spin" />
                        ) : (
                          <ArrowPathIcon className="h-5 w-5" />
                        )}
                      </span>
                    )}

                    {/* Delete */}
                    {canDeleteDocument(doc) && (
                      <button
                        onClick={(e) => handleDeleteClick(e, doc)}
                        className="p-2 -m-0.5 rounded-lg text-red-500 hover:text-red-700 hover:bg-red-50"
                        title="Delete"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    )}

                    {/* View button */}
                    {isRowClickable && (
                      <button
                        onClick={() => void handleDocumentClick(doc)}
                        className="text-xs font-bold text-gray-700 hover:text-black px-2 py-1 rounded hover:bg-gray-100"
                      >
                        {isDeletedOnline || isUnderReview(doc)
                        ? 'View'
                        : offlineEnabled && doc.status !== 'completed'
                        ? (doc.status === 'draft' || doc.status === 'pending' ? 'Resume' : 'Edit')
                        : 'View'}
                      </button>
                  )}

                  </div>
                </td>

                {/* More actions */}
                <td className="py-2 pr-2 cursor-default" onClick={(e) => e.stopPropagation()}>
                  {doc.super_form !== undefined && (offlineEnabled || isDeletedOnline) && (() => {
                    const isLocalUnsynced = !doc.synced && (typeof doc.id === 'string' || !!doc.local_id);
                    const hasPermissionError = doc.sync_error_type === 'permission';
                    return (
                      <MoreActionsMenu
                        onDuplicate={(e) => void handleDuplicateClick(e, doc)}
                        showDuplicate={isDocDuplicable(doc)}
                        showChangeProgramSite={!isDeletedOnline && (isLocalUnsynced || hasPermissionError)}
                        onChangeProgramSite={(e) => { e.stopPropagation(); setChangeProgramSiteDoc(doc); }}
                      />
                    );
                  })()}
                </td>
              </tr>
            );
          })}
            </tbody>
          </table>
        </div>
      ) : !loading ? (
        <div className="bg-gray-50 rounded-lg p-8 text-center text-gray-500">
          <p>No documents available for this client.</p>
          <p className="text-sm mt-2">Click &quot;Add New Document&quot; to create one.</p>
        </div>
      ) : null}

      {/* Change Program & Site modal */}
      <ChangeProgramSiteModal
        isOpen={!!changeProgramSiteDoc}
        onClose={() => setChangeProgramSiteDoc(null)}
        document={changeProgramSiteDoc}
        mode={changeProgramSiteDoc?.sync_error_type === 'permission' ? 'permission_error' : 'change'}
      />

      {/* Locked-online conflict-resolution modal */}
      <LockedOnlineConflictModal
        isOpen={!!conflictDoc}
        onClose={() => setConflictDoc(null)}
        document={conflictDoc}
        resolveLabel={conflictLabelResolver}
        onCreateNewDocument={handleRecreateFromConflict}
      />

      {/* Delete confirmation dialog */}
      {showDeleteConfirmation && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Delete Document</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete this document? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-4">
              <Button
                variant="tertiary"
                size="sm"
                onClick={() => {
                  setShowDeleteConfirmation(false);
                  setError(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                destructive
                size="sm"
                onClick={() => void handleDeleteConfirm()}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentsTab;
